import { useState, useEffect } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WEBSITE_STATUSES, COUPON_STATUSES, PRICING_STATUSES } from "@/lib/advertising";
import { calcEnd } from "./TermDialog";

function Pick({ label, value, onChange, options, testid }) {
  return (
    <div><Label className="text-xs">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

export default function PlacementDialog({ businessId, placement, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({});
  const [zones, setZones] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const isNew = !placement;
  useEffect(() => {
    if (!open) return;
    api.get("/mz/zone-options").then((r) => setZones(r.data.items));
    api.get("/products").then((r) => setProducts(r.data.items.filter((p) => p.active !== false)));
    api.get("/users").then((r) => setUsers(r.data.items.filter((u) => u.invite_status !== "Cancelled"))).catch(() => {});
    setF(placement ? { ...placement } : { term_months: "12", start_date: new Date().toISOString().slice(0, 10), pricing_status: "Regular", website_profile_status: "Not Started", coupon_status: "None" });
  }, [open, placement]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    const body = {
      business_id: businessId, zone_id: f.zone_id, ad_type: f.ad_type, print_position: f.print_position,
      pricing_status: f.pricing_status, monthly_price: f.monthly_price === "" || f.monthly_price == null ? null : Number(f.monthly_price),
      assigned_to: f.assigned_to, website_profile_status: f.website_profile_status, coupon_status: f.coupon_status, notes: f.notes,
    };
    if (isNew && f.add_term !== false) Object.assign(body, { term_months: Number(f.term_months), start_date: f.start_date, end_date: calcEnd(f.start_date, f.term_months) });
    try {
      if (isNew) await api.post("/placements", body); else await api.put(`/placements/${placement.id}`, body);
      toast.success(isNew ? "Placement added" : "Placement updated"); onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const zoneOpts = zones.map((z) => ({ value: z.id, label: `${z.market} › ${z.name}${z.status === "future" ? " (future)" : ""}` }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="placement-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Add Advertising Placement" : "Edit Placement"}</DialogTitle>
          <DialogDescription>One placement per zone. Company details stay on the master record.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-1">
          <div className="col-span-2"><Pick label="Market › Zone *" value={f.zone_id} onChange={set("zone_id")} options={zoneOpts} testid="pl-zone" /></div>
          <Pick label="Ad size / type *" value={f.ad_type} onChange={set("ad_type")} options={products.map((p) => p.name)} testid="pl-adtype" />
          <Pick label="Pricing" value={f.pricing_status} onChange={set("pricing_status")} options={PRICING_STATUSES} testid="pl-pricing" />
          <div><Label className="text-xs">Monthly price</Label><Input type="number" className="mt-1" value={f.monthly_price ?? ""} onChange={set("monthly_price")} data-testid="pl-monthly" /></div>
          <div><Label className="text-xs">Print position</Label><Input className="mt-1" value={f.print_position || ""} onChange={set("print_position")} data-testid="pl-position" /></div>
          <Pick label="Salesperson" value={f.assigned_to} onChange={set("assigned_to")} options={users.map((u) => ({ value: u.id, label: u.name }))} testid="pl-salesperson" />
          <Pick label="Website profile" value={f.website_profile_status} onChange={set("website_profile_status")} options={WEBSITE_STATUSES} testid="pl-website" />
          <Pick label="Coupon" value={f.coupon_status} onChange={set("coupon_status")} options={COUPON_STATUSES} testid="pl-coupon" />
          {isNew && (<>
            <Pick label="First contract length" value={f.term_months} onChange={set("term_months")} options={[{ value: "6", label: "6 months" }, { value: "12", label: "12 months" }]} testid="pl-term" />
            <div><Label className="text-xs">Start date</Label><Input type="date" className="mt-1" value={f.start_date || ""} onChange={set("start_date")} data-testid="pl-start" />
              <p className="mt-1 text-[11px] text-slate-400">Ends {calcEnd(f.start_date, f.term_months) || "—"} (editable later)</p></div>
          </>)}
          <div className="col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={2} className="mt-1" value={f.notes || ""} onChange={set("notes")} data-testid="pl-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!f.zone_id || !f.ad_type} data-testid="pl-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
